import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth, ROLE_LABEL } from "../lib/auth";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { LayoutDashboard, ScanBarcode, Package, Users, CalendarClock, FileText, BarChart3, MessageCircle, UserCog, Truck, LogOut, Building2, CalendarDays, Settings, MessageSquareText, Tags, ShoppingCart, Scissors, Wallet, Megaphone, Bed, Image, ShoppingBag } from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "manager", "billing", "grooming", "inventory"] },
  { to: "/pos", label: "POS Billing", icon: ScanBarcode, roles: ["admin", "manager", "billing"] },
  { to: "/invoices", label: "Invoices", icon: FileText, roles: ["admin", "manager", "billing"] },
  { to: "/products", label: "Products", icon: Package, roles: ["admin", "manager", "billing", "inventory"] },
  { to: "/purchases", label: "Purchases", icon: ShoppingCart, roles: ["admin", "manager", "inventory"] },
  { to: "/catalog", label: "Brands & Categories", icon: Tags, roles: ["admin", "manager", "inventory"] },
  { to: "/customers", label: "Customers", icon: Users, roles: ["admin", "manager", "billing", "grooming"] },
  { to: "/appointments", label: "Appointments", icon: CalendarClock, roles: ["admin", "manager", "billing", "grooming"] },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, roles: ["admin", "manager", "billing", "grooming"] },
  { to: "/grooming", label: "Grooming Catalog", icon: Scissors, roles: ["admin", "manager", "grooming", "billing"] },
  { to: "/boarding", label: "Boarding", icon: Bed, roles: ["admin", "manager", "grooming", "billing"] },
  { to: "/suppliers", label: "Suppliers", icon: Truck, roles: ["admin", "manager", "inventory"] },
  { to: "/financing", label: "Financing", icon: Wallet, roles: ["admin", "manager"] },
  { to: "/marketing", label: "Marketing", icon: Megaphone, roles: ["admin", "manager"] },
  { to: "/banners", label: "Storefront Banners", icon: Image, roles: ["admin", "manager"] },
  { to: "/storefront-orders", label: "Online Orders", icon: ShoppingBag, roles: ["admin", "manager"] },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["admin", "manager"] },
  { to: "/whatsapp", label: "WhatsApp", icon: MessageCircle, roles: ["admin", "manager"] },
  { to: "/branches", label: "Branches", icon: Building2, roles: ["admin"] },
  { to: "/users", label: "Staff & Roles", icon: UserCog, roles: ["admin"] },
  { to: "/settings", label: "Settings", icon: Settings, roles: ["admin"] },
];

export default function Sidebar({ onNavigate }) {
  const { user, logout, branches, currentBranchId, setCurrentBranchId } = useAuth();
  const navigate = useNavigate();
  const items = NAV.filter(n => n.roles.includes(user?.role));

  const handleBranchChange = (val) => {
    setCurrentBranchId(val);
    // Refresh current page to refetch with new branch
    window.location.reload();
  };

  return (
    <aside className="w-64 shrink-0 border-r border-border bg-card flex flex-col h-screen sticky top-0" data-testid="sidebar">
      <div className="px-5 py-5 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary text-primary-foreground grid place-items-center font-bold">P</div>
          <div>
            <div className="font-heading font-bold text-lg leading-none">Furmigo</div>
          </div>
        </div>
        {branches.length > 0 && (
          <div className="mt-3">
            <Select value={currentBranchId || ""} onValueChange={handleBranchChange} disabled={user?.role !== "admin" && branches.length === 1}>
              <SelectTrigger className="h-9 text-xs" data-testid="branch-switcher">
                <SelectValue placeholder="Select branch…" />
              </SelectTrigger>
              <SelectContent>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <NavLink
              key={it.to} to={it.to} end={it.to === "/"} onClick={onNavigate}
              data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-muted hover:text-foreground"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              <span>{it.label}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="border-t border-border p-3">
        <div className="px-2 py-2">
          <div className="font-medium text-sm" data-testid="sidebar-username">{user?.name}</div>
          <div className="text-xs text-muted-foreground">{ROLE_LABEL[user?.role] || user?.role}</div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={() => { logout(); navigate("/login"); }}
          className="w-full mt-2 flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground/70 hover:bg-muted hover:text-foreground"
        >
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>
    </aside>
  );
}
